import { activeAnalyticsConfiguration, prepareTrackingData, trackEvent } from './BBAnalytics';
import { TrackingAction, TrackingCategory } from './bnefAnalyticsSettings';

const DATA_ATTRIBUTE_TRACKING = 'data-analytics-tracking';

/**
 * Initialize all the available tracking handlers and prepare any dynamic support.
 */
export const setupAutomaticEventTrackers = () => {
    if (!activeAnalyticsConfiguration) {
        console.error('[BBAnalytics] Failed to setup automatic trackers, analytics not initialized.');
        return;
    }

    buildClickHandlers();
    prepareDynamicTrackers();
    if (activeAnalyticsConfiguration.debug) {
        console.info('[BBAnalytics] Automatic Event Trackers initialized');
    }
};

/**
 * Helper to initialize the tracking object used in automatic tracking contexts.
 */
export const buildTrackingObject = (
    action: TrackingAction,
    category: TrackingCategory = null,
    label: string = null,
    value: number = null
) => {
    return JSON.stringify(prepareTrackingData(action, category, label, value));
};
/**
 * This function relies on a MutationObserver to detect posterior DOM updates.
 * It detects which updated nodes may contain tracking targets and attaches the corresponding event handlers.
 */
const prepareDynamicTrackers = () => {
    const observer = new MutationObserver((mutations) => {
        const validMutations = mutations.filter((mutation) => mutation.addedNodes.length > 0);
        if (validMutations.length > 0) {
            buildClickHandlers();
        }
    });

    observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false,
    });
};

/**
 * This function detects all the trackable targets contained in the targetRoot and attaches
 * the corresponding analytics handler. A single reference is used for efficiency.
 */
const buildClickHandlers = () => {
    const targets = document.documentElement.querySelectorAll(`body [${DATA_ATTRIBUTE_TRACKING}]`);
    Array.from(targets).forEach((target) => {
        target.removeEventListener('click', defaultClickTracker, false);
        target.addEventListener('click', defaultClickTracker, false);
    });
};

const defaultClickTracker = function () {
    const target = this as Element;

    let trackingData: IAutomaticTrackingEventData = null;

    try {
        trackingData = JSON.parse(target.getAttribute(DATA_ATTRIBUTE_TRACKING));
    } catch {
        console.warn(`[BBAnalytics] ${DATA_ATTRIBUTE_TRACKING} is not a valid tracking object`, { trackingData });
        return;
    }

    const { action, category, label, value } = trackingData;

    if (!action || !Object.values(TrackingAction).includes(action)) {
        console.warn('[BBAnalytics] The provided action is not valid', { trackingData });
        return;
    }
    if (category && !Object.values(TrackingCategory).includes(category)) {
        console.warn('[BBAnalytics] The provided category is not valid', { trackingData });
        return;
    }
    if (value !== undefined && isNaN(value)) {
        console.warn('[BBAnalytics] The provided value is not a valid number');
        return;
    }

    trackEvent(action as TrackingAction, category as TrackingCategory, label, value);
};

export interface IAutomaticTrackingEventData {
    action?: TrackingAction;
    category?: TrackingCategory;
    label?: string;
    value?: number;
}
